// WHIT-482: package-tour search state. A search lives in the URL so results
// pages are shareable and the back button works; this module turns the URL
// query into a search, the search back into a query, and the search into the
// criteria the packageDestinationOverview / packageHotelSearch queries take.
// WHIT-484: the same search serves hotel-only stays ("Csak szállás"),
// `product: 'hotel'`; a stay has no transport, its length is in nights and
// its dates are a check-in window.
// Pure (no DOM, no React) so `node --test web/utils/packageSearch.test.js`
// can load it.

const SORTS = {
  price: { by: 'PRICE', direction: 'ASC' },
  rating: { by: 'GUEST_RATING', direction: 'DESC' },
  category: { by: 'CATEGORY', direction: 'DESC' },
};

const PAGE_SIZE = 15;
const PRODUCTS = ['package', 'hotel'];
// allmylespy's nightsBounds for a stay (api-contract.md, operation 11).
const MAX_NIGHTS = 28;
const DESTINATION_LEVELS = ['country', 'region', 'resort'];
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const CODE = /^[A-Za-z0-9_-]{1,40}$/;

/**
 * @typedef {{
 *   product: 'package' | 'hotel',
 *   dateFrom: string | null,
 *   dateTo: string | null,
 *   minDays: number,  // nights for a hotel-only stay
 *   maxDays: number,
 *   adults: number,
 *   childAges: number[],
 *   transportType: string,
 *   airports: string[],
 *   directOnly: boolean,
 *   destination: { level: string, id: string } | null,
 *   maxPrice: number | null,
 *   boardType: string | null,
 *   category: number,
 *   guestRating: number,
 *   roomTypes: string[],
 *   amenities: string[],
 *   tourOperators: string[],
 *   sort: string,
 *   page: number,
 * }} PackageSearch
 */

/** @type {PackageSearch} */
const DEFAULTS = {
  product: 'package',
  dateFrom: null,
  dateTo: null,
  minDays: 6,
  maxDays: 12,
  adults: 2,
  childAges: [],
  transportType: 'FLIGHT',
  airports: [],
  directOnly: false,
  destination: null,
  maxPrice: null,
  boardType: null,
  category: 0,
  guestRating: 0,
  roomTypes: [],
  amenities: [],
  tourOperators: [],
  sort: 'price',
  page: 1,
};

// A hotel-only search starts from the stay length allmylespy suggests
// (nightsBounds.default, 7 nights) and has no transport.
/** @type {PackageSearch} */
const HOTEL_DEFAULTS = {
  ...DEFAULTS,
  product: 'hotel',
  minDays: 7,
  maxDays: 7,
  transportType: '',
};

/**
 * @param {string} product
 * @returns {PackageSearch}
 */
const defaultsFor = product =>
  product === 'hotel' ? HOTEL_DEFAULTS : DEFAULTS;

const isHotelOnly = search => search.product === 'hotel';

const first = value => (Array.isArray(value) ? value[0] : value);

const int = (value, min, max) => {
  const raw = first(value);
  if (raw === undefined || raw === null || !/^\d+$/.test(String(raw)))
    return undefined;
  const n = Number(raw);
  return n >= min && n <= max ? n : undefined;
};

const codes = value => {
  const raw = first(value);
  if (!raw) return [];
  return String(raw)
    .split(',')
    .filter(code => CODE.test(code));
};

const code = value => {
  const raw = first(value);
  return raw && CODE.test(String(raw)) ? String(raw) : null;
};

const isoDate = value => {
  const raw = first(value);
  if (!raw || !ISO_DATE.test(String(raw))) return null;
  const date = new Date(`${raw}T00:00:00Z`);
  return Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== raw
    ? null
    : String(raw);
};

// The URL query (Next.js router.query) as a search. Anything malformed falls
// back to its default instead of reaching the API.
/**
 * @param {Record<string, string | string[] | undefined> | undefined} query
 * @returns {PackageSearch}
 */
const parseSearch = query => {
  const q = query || {};
  const product = PRODUCTS.includes(first(q.product))
    ? first(q.product)
    : 'package';
  const search = { ...defaultsFor(product) };

  search.dateFrom = isoDate(q.from);
  search.dateTo = isoDate(q.to);
  if (search.dateFrom && search.dateTo && search.dateTo < search.dateFrom) {
    search.dateTo = null;
  }

  // A package's length is in days, a stay's in nights (at most 28).
  const length = String(
    first(product === 'hotel' ? q.nights : q.days) || '',
  ).match(/^(\d{1,2})-(\d{1,2})$/);
  if (
    length &&
    Number(length[1]) >= 1 &&
    Number(length[1]) <= Number(length[2]) &&
    (product !== 'hotel' || Number(length[2]) <= MAX_NIGHTS)
  ) {
    search.minDays = Number(length[1]);
    search.maxDays = Number(length[2]);
  }

  search.adults = int(q.adults, 1, 8) ?? DEFAULTS.adults;
  search.childAges = codes(q.childAges)
    .map(age => int(age, 0, 17))
    .filter(age => age !== undefined)
    .slice(0, 4);

  if (product === 'package') {
    const transport = code(q.transport);
    if (transport) search.transportType = transport;
    search.airports = codes(q.airports);
    search.directOnly = first(q.direct) === '1';
  }

  const destination = String(first(q.dest) || '').match(
    /^(country|region|resort):([A-Za-z0-9_-]{1,40})$/,
  );
  if (destination) {
    search.destination = { level: destination[1], id: destination[2] };
  }

  search.maxPrice = int(q.maxPrice, 1, 1000000) ?? null;
  search.boardType = code(q.board);
  search.category = int(q.category, 0, 5) ?? 0;
  search.guestRating = int(q.rating, 0, 100) ?? 0;
  search.roomTypes = codes(q.rooms);
  search.amenities = codes(q.amenities);
  search.tourOperators = codes(q.operators);

  const sort = first(q.sort);
  if (sort && SORTS[sort]) search.sort = sort;
  search.page = int(q.page, 1, 1000) ?? 1;

  return search;
};

// The search as a URL query. Defaults are left out to keep the URL short; the
// order is fixed so the same search always gives the same URL.
/**
 * @param {PackageSearch} search
 * @returns {Record<string, string>}
 */
const toQuery = search => {
  const query = {};
  const hotelOnly = isHotelOnly(search);
  if (hotelOnly) query.product = 'hotel';
  if (search.dateFrom) query.from = search.dateFrom;
  if (search.dateTo) query.to = search.dateTo;
  query[hotelOnly ? 'nights' : 'days'] = `${search.minDays}-${search.maxDays}`;
  query.adults = String(search.adults);
  if (search.childAges.length) query.childAges = search.childAges.join(',');
  if (!hotelOnly) {
    if (search.transportType !== DEFAULTS.transportType)
      query.transport = search.transportType;
    if (search.airports.length) query.airports = search.airports.join(',');
    if (search.directOnly) query.direct = '1';
  }
  if (search.destination)
    query.dest = `${search.destination.level}:${search.destination.id}`;
  if (search.maxPrice) query.maxPrice = String(search.maxPrice);
  if (search.boardType) query.board = search.boardType;
  if (search.category) query.category = String(search.category);
  if (search.guestRating) query.rating = String(search.guestRating);
  if (search.roomTypes.length) query.rooms = search.roomTypes.join(',');
  if (search.amenities.length) query.amenities = search.amenities.join(',');
  if (search.tourOperators.length)
    query.operators = search.tourOperators.join(',');
  if (search.sort !== DEFAULTS.sort) query.sort = search.sort;
  if (search.page > 1) query.page = String(search.page);
  return query;
};

const isComplete = search => !!(search.dateFrom && search.dateTo);

// A search with a destination goes straight to the hotel list (P3); without
// one, to the destination overview (P2). Hotel only has no overview
// (operation 2 has no hotel-only twin), so a stay always lists hotels.
const resultsPage = search =>
  search.destination || isHotelOnly(search)
    ? '/package-tours/hotels/'
    : '/package-tours/destinations/';

// The criteria for the GraphQL queries, or null while the dates are missing.
// `maxPrice` is in whole currency units in the URL; the API takes the
// smallest unit (cents). A stay's criteria (HotelOnlySearchCriteriaInput)
// have a check-in window and nights, and no transport.
const toCriteria = search => {
  if (!isComplete(search)) return null;
  const filters = searchFilters(search);
  if (isHotelOnly(search)) {
    const criteria = {
      checkInWindow: { from: search.dateFrom, to: search.dateTo },
      nights: { min: search.minDays, max: search.maxDays },
      occupancy: { adults: search.adults, childAges: search.childAges },
    };
    if (search.destination) criteria.destination = search.destination;
    if (Object.keys(filters).length) criteria.filters = filters;
    return criteria;
  }
  const transport = { type: search.transportType };
  if (search.transportType === 'FLIGHT') {
    if (search.airports.length) transport.departureAirportIds = search.airports;
    if (search.directOnly) transport.directOnly = true;
  }

  const criteria = {
    departureWindow: { from: search.dateFrom, to: search.dateTo },
    durationDays: { min: search.minDays, max: search.maxDays },
    occupancy: { adults: search.adults, childAges: search.childAges },
    transport,
  };
  if (search.destination) criteria.destination = search.destination;
  if (Object.keys(filters).length) criteria.filters = filters;
  return criteria;
};

// The hotel filters both products share.
const searchFilters = search => {
  const filters = {};
  if (search.maxPrice) filters.maxPricePerPerson = search.maxPrice * 100;
  if (search.boardType) filters.minBoardType = search.boardType;
  if (search.category) filters.minCategory = search.category;
  if (search.guestRating) filters.minGuestRating = search.guestRating;
  if (search.roomTypes.length) filters.roomTypes = search.roomTypes;
  if (search.amenities.length) filters.amenities = search.amenities;
  if (search.tourOperators.length) filters.tourOperators = search.tourOperators;
  return filters;
};

const sortInput = search => SORTS[search.sort] || SORTS.price;
const pageInput = search => ({ number: search.page, size: PAGE_SIZE });

// WHIT-523: the departure-airport filter. A chosen airport is a search
// criterion, so allmylespy's departureAirport facet then counts only the
// chosen airports and the rest would drop out of the picker. Its options
// therefore come from the same search without the airport choice: these
// criteria, or null when no airport is chosen (the search's own facet
// serves) or the trip is not by plane (no airport to choose).
const airportFacetCriteria = search => {
  if (isHotelOnly(search) || search.transportType !== 'FLIGHT') return null;
  if (!search.airports.length) return null;
  return toCriteria({ ...search, airports: [] });
};

// The picker's options: the facet's airports, with each chosen one kept even
// when the facet has no hotel for it, so it can still be unchecked.
/**
 * @param {{ options?: { id: string, name: string, count: number }[] | null } | null | undefined} facet
 * @param {string[]} chosen
 * @returns {{ id: string, name: string, count: number }[]}
 */
const airportOptions = (facet, chosen) => {
  const options = (facet?.options || []).map(({ id, name, count }) => ({
    id,
    name,
    count,
  }));
  const listed = new Set(options.map(option => option.id));
  for (const id of chosen)
    if (!listed.has(id)) options.push({ id, name: id, count: 0 });
  return options;
};

// A change to anything but the page starts again at page 1.
/**
 * @param {PackageSearch} search
 * @param {Partial<PackageSearch>} changes
 * @returns {PackageSearch}
 */
// WHIT-499: the hotel list's active filters, one removable tag each, in the
// order the filter column lists them; `changes` is what removing that tag
// does. The destination is the search itself, not a filter, so it has none.
const LIST_FILTERS = ['roomTypes', 'amenities', 'tourOperators'];

const activeFilters = search => {
  const tags = [];
  if (search.maxPrice)
    tags.push({
      key: 'maxPrice',
      value: search.maxPrice,
      changes: { maxPrice: null },
    });
  if (search.boardType)
    tags.push({
      key: 'boardType',
      value: search.boardType,
      changes: { boardType: null },
    });
  if (search.category)
    tags.push({
      key: 'category',
      value: search.category,
      changes: { category: 0 },
    });
  if (search.guestRating)
    tags.push({
      key: 'guestRating',
      value: search.guestRating,
      changes: { guestRating: 0 },
    });
  for (const key of LIST_FILTERS)
    for (const value of search[key] || [])
      tags.push({
        key,
        value,
        changes: { [key]: search[key].filter(other => other !== value) },
      });
  return tags;
};

// "Clear filters": every filter back to its default, the search kept.
const clearFilters = () => ({
  maxPrice: DEFAULTS.maxPrice,
  boardType: DEFAULTS.boardType,
  category: DEFAULTS.category,
  guestRating: DEFAULTS.guestRating,
  roomTypes: [],
  amenities: [],
  tourOperators: [],
});

const withChanges = (search, changes) => ({
  ...search,
  page: 1,
  ...changes,
});

// Reference data destinations (country → region → resort) as one list for the
// destination typeahead, each with the path above it.
const flattenDestinations = (tree, path = []) =>
  (tree || []).flatMap(node => [
    {
      id: node.id,
      level: node.level,
      name: node.name,
      breadcrumb: path.join(' › '),
    },
    ...flattenDestinations(node.children, [...path, node.name]),
  ]);

const findDestination = (tree, destination) =>
  destination
    ? flattenDestinations(tree).find(
        node => node.id === destination.id && node.level === destination.level,
      ) || null
    : null;

// The region facet's options carry ids but no level; the reference data
// knows each id's level. Depth in the tree is the fallback.
const destinationLevel = (tree, id, depth) => {
  const known = flattenDestinations(tree).find(node => node.id === id);
  return known
    ? known.level
    : DESTINATION_LEVELS[Math.min(depth, DESTINATION_LEVELS.length - 1)];
};

// The hotel list's price choices: "any", the presets, and the limit the
// search carries when the mask's slider set one that is not a preset — so
// the active limit is always ticked.
/**
 * @param {number[]} presets
 * @param {number | null} current
 * @returns {(number | null)[]}
 */
// The filter panel's choices for a coded filter (board type, services,
// tour operator): the reference list, with the facet's counts; a value the
// facet left out has no hotel in these results. WHIT-525: uncounted (the
// destination overview, which has no facets), every value is offered
// without a count.
/**
 * @param {{ code: string, name: string }[] | null | undefined} reference
 * @param {{ options?: { id: string, name: string, count: number }[] | null } | undefined} facet
 * @param {boolean} [counted]
 * @returns {{ id: string, name: string, count?: number }[]}
 */
const filterChoices = (reference, facet, counted = true) => {
  const counts = new Map(
    ((facet && facet.options) || []).map(option => [option.id, option.count]),
  );
  if (reference && reference.length)
    return reference.map(({ code, name }) => ({
      id: code,
      name,
      count: counted ? (counts.get(code) ?? 0) : undefined,
    }));
  return (facet && facet.options) || [];
};

// The star choices: the facet's, or uncounted 1 to 5 (WHIT-525).
/**
 * @param {{ options?: { id: string, name: string, count: number }[] | null } | undefined} facet
 * @param {boolean} [counted]
 * @returns {{ id: string, name: string, count?: number }[]}
 */
const starChoices = (facet, counted = true) =>
  counted
    ? (facet && facet.options) || []
    : [1, 2, 3, 4, 5].map(stars => ({
        id: String(stars),
        name: String(stars),
      }));

const priceOptions = (presets, current) => [
  null,
  ...[
    ...presets,
    ...(current && !presets.includes(current) ? [current] : []),
  ].sort((a, b) => a - b),
];

// Every amount the API sends is in the currency's smallest unit; read the
// currency from the amount, never assume one.
const formatMoney = (money, locale) => {
  if (!money || typeof money.amount !== 'number' || !money.currency) return '';
  return new Intl.NumberFormat(locale === 'hu' ? 'hu-HU' : 'en-GB', {
    style: 'currency',
    currency: money.currency,
    maximumFractionDigits: 0,
  }).format(Math.round(money.amount / 100));
};

// Error codes the GraphQL envelope carries (api-contract.md "As built"), as
// translation keys. An unknown code still gets the generic message.
const ERROR_MESSAGES = {
  INVALID_REQUEST: 'package_error_invalid_request',
  NOT_CONFIGURED: 'package_error_unavailable',
  // WHIT-494: a tour operator answering with an error is not one that did
  // not answer in time ("did not answer in time" was shown for both).
  PROVIDER_UNAVAILABLE: 'package_error_provider_unavailable',
  PROVIDER_TIMEOUT: 'package_error_provider',
  // WHIT-501: any other upstream 5xx (the staging API, switched off,
  // answers 503) is the same outage.
  UPSTREAM_ERROR: 'package_error_provider_unavailable',
};
const errorMessage = code => ERROR_MESSAGES[code] || 'package_error_generic';

// WHIT-501: what the destination list says in place of destinations it does
// not have, as a translation key, or null. A reload keeps the list it has.
/**
 * @param {{ data: unknown, isLoading: boolean, error: { code?: string | null } | null }} reference
 * @returns {string | null}
 */
const referenceNotice = ({ data, isLoading, error }) => {
  if (data) return null;
  if (error) return errorMessage(error.code);
  return isLoading ? 'Loading...' : null;
};

// WHIT-503: a hotel chosen in the destination box. No search carries the
// 'hotel' level: a hotel opens its own page, with the dates and travellers,
// instead of a list (api-contract.md, operation 15).
const HOTEL_LEVEL = 'hotel';
// allmylespy's bounds for what was typed (operation 15).
const HOTEL_NAME_MIN_CHARACTERS = 2;
const HOTEL_NAME_MAX_LENGTH = 100;

// What to ask the hotel-name lookup for, or null while too little (or too
// much) is typed: at least two letters or digits, as allmylespy counts them.
/**
 * @param {string} text
 * @returns {string | null}
 */
const hotelNameQuery = text => {
  const typed = String(text || '').trim();
  const characters = (typed.match(/[\p{L}\p{N}]/gu) || []).length;
  return characters >= HOTEL_NAME_MIN_CHARACTERS &&
    typed.length <= HOTEL_NAME_MAX_LENGTH
    ? typed
    : null;
};

// Where a hotel is, for the line beside its name: resort, region, country,
// each once.
/**
 * @param {{ resort?: string | null, region?: string | null, country?: string | null } | null} location
 * @returns {string}
 */
const hotelPlace = location =>
  [location?.resort, location?.region, location?.country]
    .filter((part, i, parts) => part && parts.indexOf(part) === i)
    .join(', ');

/**
 * @param {{ hotelId: string, name: string | null, category: number | null, location: object | null }} hotel
 */
const hotelChoice = hotel => ({
  id: hotel.hotelId,
  level: HOTEL_LEVEL,
  name: hotel.name || '',
  breadcrumb: hotelPlace(hotel.location) || undefined,
  category: hotel.category || null,
});

// The hotels the box can offer: one without a name would be a blank row
// that opens a page nobody can recognise.
const offeredHotels = hotels =>
  (hotels || [])
    .filter(hotel => hotel && String(hotel.name || '').trim())
    .map(hotelChoice);

const isHotelChoice = destination =>
  !!destination && destination.level === HOTEL_LEVEL;

// The destination a search carries for what the box holds: a place, or
// none for a hotel (its page is the hotel).
const searchDestination = destination =>
  destination && !isHotelChoice(destination)
    ? { level: destination.level, id: destination.id }
    : null;

// WHIT-493: the first thing the search form still needs, in the order the
// fields sit, or null when it can search. The Search button stays active
// and takes the traveller there instead of greying out without saying why.
// Every child needs an age: the tour operator prices children by age.
/**
 * @param {{ dateFrom: Date | null, dateTo: Date | null, childAges?: number[] }} search
 * @returns {'dateFrom' | 'dateTo' | 'childAges' | null}
 */
const missingSearchField = ({ dateFrom, dateTo, childAges = [] }) => {
  if (!dateFrom) return 'dateFrom';
  if (!dateTo) return 'dateTo';
  if (!childAges.every(age => age >= 0)) return 'childAges';
  return null;
};

module.exports = {
  missingSearchField,
  DEFAULTS,
  HOTEL_DEFAULTS,
  defaultsFor,
  isHotelOnly,
  PAGE_SIZE,
  parseSearch,
  toQuery,
  isComplete,
  resultsPage,
  toCriteria,
  sortInput,
  pageInput,
  withChanges,
  airportFacetCriteria,
  airportOptions,
  activeFilters,
  clearFilters,
  flattenDestinations,
  findDestination,
  destinationLevel,
  priceOptions,
  filterChoices,
  starChoices,
  formatMoney,
  errorMessage,
  referenceNotice,
  hotelNameQuery,
  hotelPlace,
  hotelChoice,
  offeredHotels,
  isHotelChoice,
  searchDestination,
};
